import { parseISO } from "date-fns";
import { ExternalLink } from "lucide-react";
import { useRef } from "react";
import { useDateFormatter } from "@/hooks/useUserPreferences";
import { useTRPCClient } from "@/lib/trpc";
import { useCancelSubscription } from "@/mutations/subscription-mutations";
import { useSubscription } from "@/queries/subscription-queries";
import { AlertDialogType } from "@/types/alert-dialog";
import { type AlertDialogImperativeHandle, DefaultAlertDialog } from "../DefaultAlertDialog";
import LoadingSpinner from "../loading-spinner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import SubscriptionInvoices from "./SubscriptionInvoices";
import UpgradeSubscription from "./UpgradeSubscription";

function _formatCurrency(amount: number) {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(amount);
}

export default function ExistingSubscription() {
    const trpcClient = useTRPCClient();
    const { formatDate } = useDateFormatter();
    const { data: subscriptionData, isLoading } = useSubscription({
        includePaddleSubscription: true,
    });
    const { mutateAsync, isPending } = useCancelSubscription();
    const confirmDialogRef = useRef<AlertDialogImperativeHandle | null>(null);

    const handleCancelSubscription = async () => {
        const confirmed = await confirmDialogRef.current!.show();
        if (confirmed) {
            await mutateAsync();
        }
    };

    const handleEditBillingInformation = async () => {
        const customerPortal = await trpcClient.subscription.customerPortal.query();
        console.log("customerPortal", customerPortal);
        window.open(customerPortal.general.overview, "_blank");
    };

    if (isLoading) {
        return <LoadingSpinner />;
    }

    const isScheduledToCancel =
        subscriptionData?.paddleSubscription?.status === "active" &&
        subscriptionData?.paddleSubscription?.scheduledChange?.action === "cancel";

    if (isScheduledToCancel) {
        return (
            <>
                <UpgradeSubscription isCanceled={true} />
                <SubscriptionInvoices />
            </>
        );
    }

    console.log("subscriptionData", subscriptionData);

    return (
        <>
            <div className="border bg-white rounded-lg p-4 flex gap-6 justify-between items-center">
                <div>
                    <div className="text-md font-semibold">
                        <Badge variant="default" className="text-sm bg-blue-500 py-0.5 px-1 rounded-sm">
                            Pro
                        </Badge>
                    </div>
                </div>
                <div className="mr-auto">
                    <div className="flex gap-6 text-sm">
                        <div>
                            <div className="text-neutral-500">Users</div>
                            <div className="font-medium">{subscriptionData?.usersInUse}</div>
                        </div>
                        <div>
                            <div className="text-neutral-500">Renewal date</div>
                            <div className="font-medium">
                                {!!subscriptionData?.paddleSubscription?.nextBilledAt &&
                                    formatDate(parseISO(subscriptionData!.paddleSubscription!.nextBilledAt))}
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <Button
                        disabled={isPending}
                        type="button"
                        className="hover:text-red-600"
                        variant="ghost"
                        onClick={handleCancelSubscription}
                    >
                        {isPending ? <LoadingSpinner /> : null}
                        Cancel subscription
                    </Button>
                </div>
            </div>

            <div className="border bg-white rounded-lg p-4 flex justify-between items-center">
                <div className="text-sm">
                    <div className="font-medium">Billing information</div>
                </div>
                <div></div>
                <div>
                    <Button type="button" variant="ghost" onClick={handleEditBillingInformation}>
                        Customer portal on Paddle
                        <ExternalLink />
                    </Button>
                </div>
            </div>
            <SubscriptionInvoices />
            <DefaultAlertDialog
                ref={confirmDialogRef}
                type={AlertDialogType.Confirm}
                title="Cancel subscription"
                description="Are you sure you want to cancel your subscription? You will lose access to your tasks. Your subscription will end on the next renewal date."
            />
        </>
    );
}
